import type { ReturnType } from 'birpc'
import { buildGa4ItemCategoryParams } from '~/utils/ga4-category'

// GA4 ecommerce item shape used by cart-related events
// (add_to_cart, remove_from_cart, view_cart, click_cart_suggestion_add).
export interface Ga4CartItem {
  item_id: string
  item_name: string
  item_brand: string
  item_supplier: string
  item_category: string
  item_category_2?: string
  item_category_3?: string
  item_category_4?: string
  item_category_5?: string
  item_variant: string
  price?: number
  quantity?: number
  promotion: 'yes' | 'no'
  index: number
  reference_value?: 'office' | 'pharmacy'
  gpo_recommended: 'yes' | 'no'
  item_list_id?: string
  item_list_name?: string
}

// GA4 ecommerce item shape used by the PDP `view_item` event.
export type Ga4ViewItem = {
  item_id: string
  item_name: string
  item_brand: string
  item_variant: string
  price?: number
  quantity: number
  promotion: 'yes' | 'no'
  index: number
  reference_value?: 'office' | 'pharmacy'
  gpo_recommended: 'yes' | 'no'
} & Partial<Record<
  'item_category' | 'item_category2' | 'item_category3' | 'item_category4' | 'item_category5',
  string
>>

export interface Ga4ViewItemInput {
  product: {
    handle: string
    name?: string
    campaign?: { status?: string }
    reference?: string
  }
  price?: number
  fields?: Record<string, string>
  supplierName?: string
  categoryLevels?: Array<string | undefined | null>
  quantity?: number
  reference?: 'office' | 'pharmacy'
  isGpoRecommended?: boolean
  index?: number
}

export function resolveItemVariant(fields?: Record<string, string>): string {
  if (!fields) return ''
  return Object.entries(fields)
    .filter(([key]) => key !== 'reference')
    .map(([_, value]) => value)
    .join(' / ')
}
export interface Ga4CartItemInput {
  handle: string
  name?: string
  reference?: string
  quantity?: number
  listPrice?: { amount?: number }
  scalePrices?: any[]
  isActiveCampaign?: boolean
  campaign?: { status?: string, after_price?: number, afterPrice?: number }
  /** Localized category path as string labels, or locale maps (`{ en, de, ... }`). */
  categories?: Array<Array<string | Record<string, string>>>
}

export function useHelper() {
  const { withSuffix } = useNetPriceLabel()

  return {
    currencyToString(valueInCent: number, options?: { netSuffix?: boolean }) {
      const currencyFormatter = new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
      })
      const valueInEuro = valueInCent ? valueInCent / 100 : 0
      const formatted = currencyFormatter.format(valueInEuro)
      if (options?.netSuffix === false)
        return formatted
      return withSuffix(formatted)
    },
    removeUnderscoresAndCapitalize(text: string) {
      return text
        .replaceAll(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    capitalizeFirstLetter(word: string) {
      return word.replace(/^./, word[0].toUpperCase())
    },
    locationQueryToUrlSearchParams(query: Record<string, string | string[]>) {
      const searchParams = new URLSearchParams()
      for (const [key, value] of Object.entries(query)) {
        if (value == null)
          continue
        if (Array.isArray(value)) {
          for (const item of value) searchParams.append(key, item)
        }
        else {
          searchParams.append(key, value)
        }
      }
      return searchParams
    },
    abbreviateArray(items: string[]) {
      if (items && Array.isArray(items) && items.length > 3) {
        const appendix = `(+${items.length - 3} weitere)`
        const abbreviated = items.slice(0, 3)
        let returnVal = abbreviated.join('; ')
        returnVal = returnVal.concat(' ', appendix)
        return returnVal
      }

      if (items)
        return items.join('; ')
      return undefined
    },

    // Convert a PDP product to the GA4 ecommerce item shape used by the
    // `view_item` event.
    convertProductToGa4ViewItem({
      product,
      price,
      fields,
      supplierName,
      categoryLevels,
      quantity,
      reference,
      isGpoRecommended,
      index = 1,
    }: Ga4ViewItemInput): Ga4ViewItem {
      return {
        item_id: product.handle,
        item_name: product.name || 'Unknown',
        item_brand: supplierName || 'Unknown',
        ...buildGa4ItemCategoryParams(categoryLevels ?? []),
        item_variant: resolveItemVariant(fields),
        price,
        quantity: quantity ?? 1,
        promotion: product.campaign?.status === 'ACTIVE' ? 'yes' : 'no',
        index,
        ...(reference === 'office' || reference === 'pharmacy'
          ? { reference_value: reference }
          : {}),
        gpo_recommended: isGpoRecommended ? 'yes' : 'no',
      }
    },

    // Convert product to ga4 recommended structure
    convertProductToGa4EcommerceItem({
      index = undefined,
      data,
      supplierName,
      price,
      selectedFields,
      $i18n,
    }: {
      $i18n: ReturnType<typeof useNuxtApp>['$i18n']
      index?: number
      data: any
      supplierName?: string
      price?: number
      selectedFields?: Record<string, string>
    }) {
      return {
        item_id: data.handle,
        item_name: data.name || 'Unknown',
        item_brand: supplierName || 'Unknown',
        item_category: data.category?.name ? $i18n.t(data.category.name) : 'Unknown',
        price,
        quantity: data.quantity,
        item_variant: resolveItemVariant(selectedFields),
        discount: 0,
        coupon: undefined,
        index,
      }
    },

    // Convert a cart item to the GA4 ecommerce item shape used by cart events.
    convertCartItemToGa4EcommerceItem(
      item: Ga4CartItemInput,
      supplierName: string,
      index: number,
      ctx: {
        $i18n: ReturnType<typeof useNuxtApp>['$i18n']
        isGpoCompliant: (item: Ga4CartItemInput) => boolean
      },
    ): Ga4CartItem {
      const price = item.listPrice?.amount ? item.listPrice.amount / 100 : undefined
      const referenceValue = item.reference?.toLowerCase()
      // GA4 reports are analyzed centrally in English, so cart-event category
      // names are emitted in `en` regardless of the active UI locale.
      const categoryPath = item.categories?.[0] ?? []
      const localizedCategory = (level: number): string | undefined => {
        const entry = categoryPath[level]
        if (typeof entry === 'string')
          return entry || undefined
        return entry?.en ?? undefined
      }

      return {
        item_id: item.handle,
        item_name: item.name || 'Unknown',
        // TODO: add manufacturer name when we have a way to get the manufacturer from the cart item.
        item_brand: '',
        item_supplier: supplierName || 'Unknown',
        item_category: localizedCategory(0) ?? 'Unknown',
        ...(localizedCategory(1) && { item_category_2: localizedCategory(1) }),
        ...(localizedCategory(2) && { item_category_3: localizedCategory(2) }),
        ...(localizedCategory(3) && { item_category_4: localizedCategory(3) }),
        ...(localizedCategory(4) && { item_category_5: localizedCategory(4) }),
        // TODO: add item_variant when we have a way to get the variant from the cart item.
        item_variant: '',
        price,
        quantity: item.quantity,
        promotion: item.campaign?.status === 'ACTIVE' ? 'yes' : 'no',
        index,
        ...(referenceValue === 'office' || referenceValue === 'pharmacy'
          ? { reference_value: referenceValue }
          : {}),
        gpo_recommended: ctx.isGpoCompliant(item) ? 'yes' : 'no',
      }
    },
    resolveItemVariant: resolveItemVariant,
  }
}
